import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AuthShell, Field, FormError, SubmitButton, AuthLink } from "@/components/auth/AuthShell";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

export default function Apply() {
  const [opts, setOpts] = useState({ license_types: [], states: [] });
  const [form, setForm] = useState({ name: "", email: "", phone: "", license_type: "", license_number: "", license_state: "", practice_name: "", notes: "", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/applications/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.license_type || !form.license_state) { setError("Please choose your license type and state"); return; }
    if (!form.consent) { setError("Please confirm the information is accurate"); return; }
    setLoading(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      toast.success("Application received");
    } catch (err) { setError(errorMessage(err)); } finally { setLoading(false); }
  };

  if (done) {
    return (
      <AuthShell title="Application received" footer={<AuthLink to="/" testId="apply-home-link">Back to home</AuthLink>}>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-6 space-y-3" data-testid="apply-success">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          <p className="text-emerald-900 font-medium">Thank you, {form.name.split(" ")[0]}. We verify every license by hand, usually within 1–2 business days.</p>
          <p className="text-sm text-emerald-800">You will receive an email at <strong>{form.email}</strong> with a secure link to set your password once you are approved.</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Apply for clinician access" subtitle="For licensed therapists and therapist associates. Clients join later by your invitation."
      footer={<>Already approved? <AuthLink to="/login" testId="apply-login-link">Sign in</AuthLink></>}>
      <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
        <Field id="name" label="Full name" value={form.name} onChange={set("name")} autoComplete="name" testId="apply-name-input" />
        <Field id="email" label="Work email" type="email" value={form.email} onChange={set("email")} autoComplete="email" testId="apply-email-input" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>License type</Label>
            <Select value={form.license_type} onValueChange={set("license_type")}>
              <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="apply-license-type-select"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>{opts.license_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>State licensed in</Label>
            <Select value={form.license_state} onValueChange={set("license_state")}>
              <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="apply-state-select"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent className="max-h-72">{opts.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Field id="license_number" label="License number" value={form.license_number} onChange={set("license_number")} testId="apply-license-number-input" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label htmlFor="practice">Practice name <span className="text-xs text-slate-400">(optional)</span></Label><input id="practice" value={form.practice_name} onChange={(e) => set("practice_name")(e.target.value)} className="flex h-12 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm" data-testid="apply-practice-input" /></div>
          <div className="space-y-2"><Label htmlFor="phone">Phone <span className="text-xs text-slate-400">(optional)</span></Label><input id="phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="flex h-12 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm" data-testid="apply-phone-input" /></div>
        </div>
        <div className="space-y-2"><Label htmlFor="notes">Anything we should know? <span className="text-xs text-slate-400">(optional)</span></Label><Textarea id="notes" value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} className="rounded-xl bg-white" data-testid="apply-notes-input" /></div>
        <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.consent} onChange={(e) => set("consent")(e.target.checked)} className="mt-1 w-4 h-4 accent-indigo-600" data-testid="apply-consent-checkbox" />
          <span>I confirm I hold the license described above, that the information is accurate, and that it may be checked against my state licensing board.</span>
        </label>
        <FormError message={error} />
        <SubmitButton loading={loading} testId="apply-submit-button">Submit application</SubmitButton>
      </form>
    </AuthShell>
  );
}
